import { Variant2 } from '@/@types/global';
import { AddToCartPayload, Cart, CartPayloads, UpdateCartPayload, Variant } from '@/@types/shopify';

type PurchaseGiftManagerConfig = {
  required_amount: number;
  price_threshold: number;
  quantity_to_add: number;
  id: number[];
};

export type AddInterceptConfig = {
  name: string;
  position: {
    relative: 'AFTER' | 'BEFORE';
    event: string;
  };
  onAction: (cart: Cart) => Promise<boolean>;
};

export type InterceptConfig = {
  name: string;
  action: (cart: Cart) => Promise<boolean>;
};

export type UpnovaChainEvents = {
  cart: {
    note: string;
    attributes: {};
    original_total_price: number;
    total_price: number;
    total_discount: number;
    total_weight: number;
    item_count: number;
    items: Array<any>;
    requires_shipping: boolean;
    currency: string;
    items_subtotal_price: number;
    cart_level_discount_applications: Array<any>;
    checkout_charge_amount: number;
  };
  addIntercept: (config: AddInterceptConfig) => void;
  getAllChainEvents: () => InterceptConfig[];
  updateCart: (cart: Cart) => void;
  onCartChanged: (cart: Cart) => void;
};

declare global {
  interface Window {
    upez__chainEventManager: UpnovaChainEvents;
  }
}

type ModalStatus = 'not-eligible' | 'not-needed' | 'user-closed' | 'add-multiple' | 'user-rejected';

type ShowModalVariantRequirements = {
  price: number;
  inventory_quantity: number;
};

type ShowModalOverrides = {
  currentVariantId: string;
  variant: ShowModalVariantRequirements;
  qty: number;
  packMultiple: string;
  modalViewport: HTMLElement;
  skipLabel: string;
};

const PromotionTag = 'multi-buy';

export default class PurchaseGiftManager {
  private defaults: PurchaseGiftManagerConfig = {
    required_amount: 3,
    price_threshold: 26,
    quantity_to_add: 3,
    id: [],
  };

  private config: PurchaseGiftManagerConfig = Object.assign(this.defaults, {});

  deferJS(method: Function, validator: Function) {
    let loadRunner = function () {
      if (typeof validator !== 'function') return false;

      if (validator()) {
        method();
        return false;
      }

      setTimeout(function () {
        loadRunner();
      }, 50);
    };
    loadRunner();
  }

  constructor(_config: Partial<PurchaseGiftManagerConfig>) {
    this.config = Object.assign(this.defaults, _config);

    // this.deferJS(
    //   () => {
    //     window.upez__chainEventManager.addIntercept({
    //       name: 'B3G3_GIFT_CHECK',
    //       position: {
    //         relative: 'AFTER',
    //         event: 'GIFT_ADD_CHECK',
    //       },
    //       onAction: async (cart: Cart) => {
    //         if (!cart) return true;

    //         const newCart = await this.onCartChange(cart);

    //         if (newCart) window.upez__chainEventManager.updateCart(newCart);

    //         return true;
    //       },
    //     });
    //   },
    //   () => {
    //     return (
    //       !!window?.upez__chainEventManager?.addIntercept &&
    //       window.upez__chainEventManager
    //         .getAllChainEvents()
    //         .find((chainEvent) => chainEvent.name === 'GIFT_ADD_CHECK') !== undefined
    //     );
    //   },
    // );
  }

  async pushPayloadToCart(endpoint: string, payload: CartPayloads) {
    let response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    let responseJson = await response.json();

    return responseJson;
  }

  async updateCart(updates: FormData): Promise<Cart> {
    let response = await fetch('/cart/update.js', {
      method: 'POST',
      body: updates,
    });

    if (response.ok) {
      return await response.json();
    }

    throw new Error('Failed to update cart');
  }

  getConfig(): PurchaseGiftManagerConfig {
    return { ...this.config };
  }

  /**
   * Checks if the cart is eligible for multi-buy bundle gifts
   * @param cart The cart to check
   * @returns An object containing the eligibility status and the list of eligible gift variant IDs
   */
  checkEligibility(cart: Cart | null = null): {
    eligible: boolean;
    gifts: number[];
    ineligibleGifts: number[];
  } {
    const items = cart?.items || [];

    const allGifts = window?.theme?.packOptions?.bundles?.reduce((all, curr) => {
      all.push(...curr.bundle_gifts);
      return all;
    }, [] as number[]);

    // Only consider items meeting the price threshold (exclude free gifts and low-priced items)
    const eligibleItems = items.filter((item) => {
      const currencyRate = parseFloat(window.Shopify.currency.rate || '1');

      if (!currencyRate) {
        console.error('Could not find currency rate!');
        return false;
      }

      const priceDollars = item.price / 100;

      // Convert price to base currency - inflated currencies will have a rate less than 1 to scale back to base currency
      const baseCurrencyPrice =
        currencyRate < 1 ? priceDollars / currencyRate : priceDollars * currencyRate;

      // console.debug(`${item.title} Raw price: $${priceDollars}`);
      // console.debug(`${item.title} Base currency price: $${baseCurrencyPrice}`);

      const eligible = baseCurrencyPrice >= window.theme.gift_product.price_threshold;

      // console.debug(`${item.title} Eligible: ${eligible}`);

      return eligible;
    });

    if (eligibleItems.length === 0) {
      this.config.id = [];
      return {
        eligible: false,
        gifts: [],
        ineligibleGifts: allGifts || [],
      };
    }

    // Group by variant_id to account for multiple lines of the same variant (e.g., different properties)
    const qtyByVariant = new Map<number, number>();
    eligibleItems.forEach((item) => {
      qtyByVariant.set(item.variant_id, (qtyByVariant.get(item.variant_id) || 0) + item.quantity);
    });

    // console.debug('qtyByVariant', qtyByVariant);

    // Find the single variant with the highest total quantity
    const maxEntry = Array.from(qtyByVariant.entries()).reduce<{
      variantId: number;
      qty: number;
    } | null>((acc, [variantId, qty]) => {
      if (!acc || qty > acc.qty) return { variantId, qty };
      return acc;
    }, null);

    // console.debug('maxEntry', maxEntry);

    const highestQty = maxEntry?.qty || 0;

    // Determine the eligible pack based on item_count vs highestQty
    const packOptions = window?.theme?.packOptions?.bundles;

    if (!packOptions || packOptions.length === 0) {
      this.config.id = [];
      return {
        eligible: false,
        gifts: [],
        ineligibleGifts: allGifts || [],
      };
    }

    // Choose the best pack: the one with the largest item_count <= highestQty
    const eligiblePack = packOptions
      .filter((p: any) => typeof p.item_count === 'number' && p.item_count <= highestQty)
      .sort((a: any, b: any) => b.item_count - a.item_count)[0];

    if (!eligiblePack) {
      console.debug('No eligible tier found');
      this.config.id = [];
      return {
        eligible: false,
        gifts: [],
        ineligibleGifts: allGifts || [],
      };
    }

    if (eligiblePack.bundle_gifts.length === 0) {
      console.debug('No gifts found for eligible tier');
      this.config.id = [];
      return {
        eligible: false,
        gifts: [],
        ineligibleGifts: allGifts || [],
      };
    }

    console.debug('Eligible tier found', eligiblePack);

    // Update configured gift IDs to the selected pack's gifts
    const giftIds: number[] = Array.isArray(eligiblePack.bundle_gifts)
      ? eligiblePack.bundle_gifts.map((id: any) => +id).filter((n: number) => !isNaN(n))
      : [];

    const otherGifts: number[] = packOptions.reduce((all, curr) => {
      all.push(...curr.bundle_gifts.filter((gift) => giftIds.indexOf(gift) === -1));
      return all;
    }, [] as number[]);

    console.debug('Other gifts', otherGifts);

    this.config.id = giftIds;

    return {
      eligible: true,
      gifts: giftIds,
      ineligibleGifts: otherGifts,
    };
  }

  async removeGifts(cart: Cart | null = null, gifts?: number[]): Promise<Cart> {
    if (!cart) {
      cart = await window.Shopify.getCart();
    }

    let giftsInCart = cart.items.filter(
      (item) =>
        (gifts ? gifts.includes(item.variant_id) : item.properties._promotion == PromotionTag) &&
        item.price == 0,
    );

    if (!giftsInCart.length) return cart;

    var formData = new FormData();

    giftsInCart.forEach((item) => {
      formData.append('updates[' + item.id + ']', '0');
    });

    const updateResult = await this.updateCart(formData);

    return updateResult;
  }

  async addGiftsToCart(gifts: number[], cart: Cart | null = null): Promise<Cart | null> {
    if (!cart) {
      cart = await window.Shopify.getCart();
    }

    let giftsNotInCart = gifts.filter((id) => !cart.items.find((item) => item.variant_id === id));

    if (giftsNotInCart.length === 0) return cart;

    let giftsPayload = giftsNotInCart.map((id) => ({
      id: id,
      quantity: 1,
      properties: {
        Promotion: window.theme.packOptions?.labels.cart_message,
        _promotion: PromotionTag,
        _source: 'purchase-gift-manager:addGiftsToCart',
      },
    }));

    let newCart: Cart | null = null;

    if (giftsPayload.length > 0) {
      newCart = await this.pushPayloadToCart('/cart/add.js', {
        items: giftsPayload,
      });
    }

    let giftsInCart = cart.items.filter((item) => this.config.id.indexOf(item.variant_id) > -1);

    giftsPayload = [];
    giftsInCart
      .filter((item) => item.quantity !== 1)
      .map((item) => ({
        id: item.variant_id,
        quantity: 1,
        properties: {
          _promotion: PromotionTag,
        },
      }));

    if (giftsPayload.length > 0) {
      newCart = await this.pushPayloadToCart('/cart/change.js', {
        items: giftsPayload,
      });
    }

    return null;
  }

  showModal(overrides: Partial<ShowModalOverrides> = {}): Promise<{ status: ModalStatus }> {
    return new Promise((resolve) => {
      let response = {
        status: 'not-eligible' as ModalStatus,
      };

      let currentVariantId: string = overrides.currentVariantId ?? '';
      if (!currentVariantId) {
        try {
          currentVariantId =
            document.querySelector<HTMLInputElement>(".product-form__variants [name='id']")
              ?.value ?? '';
        } catch (e) {
          console.error('[PurchaseGiftManager.showModal] Unable to resolve currentVariantId');
        }
      }

      let variant: ShowModalVariantRequirements | undefined = overrides.variant;
      if (!variant && currentVariantId) {
        try {
          const pdpVariant = window.PDP?.variants?.find(function (variant: Variant2) {
            return +variant.id === +currentVariantId;
          });
          if (pdpVariant) {
            variant = {
              price: pdpVariant.price,
              inventory_quantity: pdpVariant.inventory_quantity ?? 0,
            };
          }
        } catch (e) {
          console.error(
            '[PurchaseGiftManager.showModal] Unable to resolve variant from window.PDP',
          );
        }
      }

      if (!variant) {
        console.error(
          '[PurchaseGiftManager.showModal] Missing variant, cannot evaluate eligibility',
        );
        return resolve(response);
      }

      if (
        variant.price / 100 < this.config.price_threshold ||
        variant.inventory_quantity < this.config.required_amount
      ) {
        return resolve(response);
      }

      let qty: number | undefined = overrides.qty;
      if (typeof qty !== 'number' || Number.isNaN(qty)) {
        try {
          const qtyInput = document.querySelector<HTMLInputElement>(
            '.product-form__quantity .product-form__quantity-input',
          );
          qty = qtyInput?.value ? +qtyInput.value : 1;
        } catch (e) {
          console.error('[PurchaseGiftManager.showModal] Unable to resolve qty; defaulting to 1');
          qty = 1;
        }
      }

      let packMultiple: string = overrides.packMultiple ?? '';
      if (!packMultiple) {
        try {
          packMultiple =
            document
              .querySelector('[data-bundle-option].active')
              ?.getAttribute('data-pack-multiple') || '1';
        } catch (e) {
          console.error(
            '[PurchaseGiftManager.showModal] Unable to resolve packMultiple; defaulting to 1',
          );
          packMultiple = '1';
        }
      }

      // Don't show modal if the qty input is already 3 or higher
      // Continue as if they clicked No
      if (qty * parseInt(packMultiple) >= this.config.required_amount) {
        response.status = 'not-needed';
        return resolve(response);
      }

      let skipLabel: string = overrides.skipLabel ?? '';
      if (!skipLabel) {
        try {
          skipLabel =
            window.theme?.labels?.product_gift_skip_cta_label?.replace('{qty}', qty.toString()) ||
            window.theme?.labels?.addToCart ||
            '';
        } catch (e) {
          console.error('[PurchaseGiftManager.showModal] Unable to resolve skipLabel');
          skipLabel = '';
        }
      }

      let modalViewport: HTMLElement | null = overrides.modalViewport ?? null;
      if (!modalViewport) {
        try {
          modalViewport = document.querySelector<HTMLElement>('.product-gift-modal__viewport');
        } catch (e) {
          console.error('[PurchaseGiftManager.showModal] Unable to resolve modalViewport');
          modalViewport = null;
        }
      }

      if (!modalViewport) {
        console.error(
          '[PurchaseGiftManager.showModal] Missing modal viewport (.product-gift-modal__viewport)',
        );
        return resolve(response);
      }

      const closeButton = modalViewport.querySelector<HTMLElement>(
        '.product-gift-modal__close-btn',
      );
      const addMultipleCta = modalViewport.querySelector<HTMLElement>('.add-required-to-cart');
      const addOneCta = modalViewport.querySelector<HTMLElement>('.continue-add-to-cart');

      if (!closeButton || !addMultipleCta || !addOneCta) {
        console.error('[PurchaseGiftManager.showModal] Missing modal buttons inside viewport');
        return resolve(response);
      }

      if (skipLabel) {
        addOneCta.textContent = skipLabel;
      }

      const VISIBLE_CLASS = 'product-gift-modal__viewport--visible';
      const backdrop = modalViewport.querySelector<HTMLElement>('.product-gift-modal__backdrop');

      const hideModal = () => {
        modalViewport.classList.remove(VISIBLE_CLASS);
        modalViewport.setAttribute('aria-hidden', 'true');
        if (backdrop) backdrop.setAttribute('aria-hidden', 'true');
      };

      const cleanup = () => {
        closeButton.removeEventListener('click', onClose);
        addMultipleCta.removeEventListener('click', onAddMultiple);
        addOneCta.removeEventListener('click', onAddOne);
        if (backdrop) backdrop.removeEventListener('click', onBackdrop);
      };

      const onClose = (e: MouseEvent) => {
        e.preventDefault();
        hideModal();
        response.status = 'user-closed';
        cleanup();
        return resolve(response);
      };

      const onAddMultiple = (e: MouseEvent) => {
        e.preventDefault();
        hideModal();
        response.status = 'add-multiple';
        cleanup();
        return resolve(response);
      };

      const onAddOne = (e: MouseEvent) => {
        e.preventDefault();
        hideModal();
        response.status = 'user-rejected';
        cleanup();
        return resolve(response);
      };

      const onBackdrop = () => {
        hideModal();
        response.status = 'user-closed';
        cleanup();
        return resolve(response);
      };

      cleanup();
      closeButton.addEventListener('click', onClose);
      addMultipleCta.addEventListener('click', onAddMultiple);
      addOneCta.addEventListener('click', onAddOne);
      if (backdrop) backdrop.addEventListener('click', onBackdrop);

      modalViewport.classList.add(VISIBLE_CLASS);
      modalViewport.setAttribute('aria-hidden', 'false');
      modalViewport.setAttribute('role', 'dialog');
      modalViewport.setAttribute('aria-modal', 'true');
      if (backdrop) backdrop.setAttribute('aria-hidden', 'false');
    });
  }

  async addRequiredForGiftToCart(elem: HTMLElement): Promise<void> {
    const variantId = elem.getAttribute('data-variant-id') ?? '0';

    let cart = await window.Shopify.getCart();

    let quantityInCart = cart.items
      .filter((item) => item.variant_id.toString() == variantId)
      .reduce((qtyInCart, currentItem) => {
        return qtyInCart + currentItem.quantity;
      }, 0);

    console.debug('Quantity in cart: ', quantityInCart);

    let parsedGifts = await this.parseCartGifts(cart);

    return new Promise((resolve, reject) => {
      try {
        if (quantityInCart >= this.config.required_amount) return resolve();

        let payload: AddToCartPayload = {
          items: [
            {
              id: variantId,
              quantity: this.config.required_amount,
              // Promo label is added in addPromoLabels
            },
          ],
        };

        if (parsedGifts.notInCart.gifts.length)
          payload.items.push(...parsedGifts.notInCart.payload);

        return resolve(this.pushPayloadToCart(parsedGifts.notInCart.endpoint, payload));
      } catch (e) {
        return reject(e);
      }
    });
  }

  async parseCartGifts(cart: Cart | null = null): Promise<{
    notInCart: {
      endpoint: string;
      payload: AddToCartPayload['items'];
      gifts: number[];
    };
    inCart: { endpoint: string; payload: UpdateCartPayload; gifts: number[] };
  }> {
    let returnValue: {
      notInCart: {
        endpoint: string;
        payload: AddToCartPayload['items'];
        gifts: number[];
      };
      inCart: { endpoint: string; payload: UpdateCartPayload; gifts: number[] };
    } = {
      notInCart: {
        endpoint: `${window.Shopify.routes.root}cart/add.js`,
        payload: [],
        gifts: [] as number[],
      },
      inCart: {
        endpoint: `${window.Shopify.routes.root}cart/update.js`,
        payload: {},
        gifts: [] as number[],
      },
    };
    if (!cart) {
      cart = await window.Shopify.getCart();
    }

    if (typeof this.config.id === 'string') this.config.id = [this.config.id];

    returnValue.notInCart.gifts = this.config.id.filter(
      (id) => !cart.items.find((item) => item.variant_id === id),
    );

    if (returnValue.notInCart.gifts.length) {
      returnValue.notInCart.payload = [] as AddToCartPayload['items'];
      returnValue.notInCart.gifts.map((id) => ({
        id: id,
        quantity: 1,
        properties: {
          _promotion: PromotionTag,
          _source: 'purchase-gift-manager:parseCartGifts',
        },
      }));
    }

    returnValue.inCart.gifts = cart.items
      .filter((item) => this.config.id.indexOf(item.variant_id) > -1)
      .map((item) => item.variant_id);

    returnValue.inCart.payload = returnValue.inCart.gifts.reduce(
      (acc: UpdateCartPayload, curr: number) => {
        acc[curr] = 1;

        return acc;
      },
      {} as UpdateCartPayload,
    );

    return returnValue;
  }

  async onCartChange(cart: Cart | null = null): Promise<Cart | null> {
    if (!cart) {
      cart = await window.Shopify.getCart();
    }

    let eligibilityCheck = this.checkEligibility(cart);

    if (!eligibilityCheck.eligible) {
      return this.removeGifts(cart);
    }

    if (eligibilityCheck.gifts.length === 0) return cart;

    let newCart = await this.addGiftsToCart(eligibilityCheck.gifts, cart);

    newCart = await this.removeGifts(newCart, eligibilityCheck.ineligibleGifts);

    return newCart;
  }

  async onVariantChange(variant: Variant | null = null) {
    if (!variant) {
      throw new Error('No variant provided');
    }

    let variant_inventory_quantity =
      window.PDP.variants?.find(function (liquidVariant: Variant2) {
        return liquidVariant.available && liquidVariant?.id === variant?.id;
      })?.inventory_quantity || -1;

    const hitpoint = document.querySelector<HTMLElement>('.purchase-gift__hitpoint');
    if (!hitpoint) return;

    const shouldHide =
      variant_inventory_quantity < this.config.required_amount ||
      variant.price / 100 < this.config.price_threshold;

    hitpoint.classList.toggle('hidden', shouldHide);
    hitpoint.setAttribute('data-variant-id', String(variant.id));
  }

  async sortGiftsByCartStatus(
    gifts?: string[],
    cart: Cart | null = null,
  ): Promise<{ inCart: string[]; notInCart: string[] }> {
    if (!cart) {
      cart = await window.Shopify.getCart();
    }

    if (!gifts) {
      return {
        inCart: [],
        notInCart: [],
      };
    }

    let inCart = cart.items
      .filter((item) => gifts.indexOf(item.variant_id.toString()) > -1)
      .map((item) => item.variant_id.toString());

    let notInCart = gifts.filter((id) => !inCart.includes(id));

    return { inCart, notInCart };
  }

  createGiftsPayloads(gifts: { inCart: string[]; notInCart: string[] }): {
    notInCart: AddToCartPayload['items'];
    inCart: UpdateCartPayload;
  } {
    let retVal: {
      notInCart: AddToCartPayload['items'];
      inCart: UpdateCartPayload;
    } = {
      notInCart: gifts.notInCart.map((id) => ({
        id,
        quantity: 1,
        properties: {
          Promotion: window.theme.packOptions?.labels.cart_message,
          _promotion: PromotionTag,
          _source: 'purchase-gift-manager:createGiftsPayloads',
        },
      })),
      inCart: gifts.inCart.reduce((acc, curr) => {
        acc[curr] = 1;
        return acc;
      }, {} as UpdateCartPayload),
    };

    return retVal;
  }
}
